import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Wordmark } from '../ui'
import { attorneyApi } from '../api'

// Admin Founding-Membership decision page, reached from the email link
// (/founding-review/:token). Public — the signed token is the authorization.
// One review covers the bar check AND the founding decision: Approve puts the
// listing live FREE and decrements the public founding counter; Decline sends
// the rejection email (support contact included) and touches nothing else.
const CO_SEARCH = 'https://www.coloradolegalregulation.com/attorney-search/'

function Shell({ children }) {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 font-sans text-slate-900">
      <header className="border-b border-slate-200 bg-white/90 px-5 py-3.5"><Wordmark /></header>
      <main className="flex flex-1 items-center justify-center px-5 py-10">
        <div className="w-full max-w-md">{children}</div>
      </main>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right font-medium text-slate-900">{value || '—'}</dd>
    </div>
  )
}

export function FoundingReview() {
  const { token } = useParams()
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState('')

  useEffect(() => {
    attorneyApi.foundingReviewLookup(token).then(setData)
      .catch((e) => setError(e.message || 'This review link is invalid or expired.'))
  }, [token])

  const decide = async (action) => {
    setBusy(action); setError('')
    try { setResult(await attorneyApi.foundingReviewDecide(token, action)) }
    catch (e) { setError(e.message || 'Could not save your decision.') }
    setBusy('')
  }

  if (error && !data) {
    return <Shell><div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      <p className="text-sm text-slate-600">{error}</p></div></Shell>
  }
  if (!data) return <Shell><p className="text-center text-sm text-slate-500">Loading…</p></Shell>

  if (result) {
    const approved = result.status === 'active'
    const remaining = result.seats?.founding_remaining
    return <Shell><div className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
      <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full text-2xl ${approved ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>{approved ? '✓' : '—'}</div>
      <h1 className="text-xl font-bold tracking-tight">{approved
        ? `${data.name} is a Founding Member`
        : `${data.name}'s application declined`}</h1>
      <p className="mt-2 text-sm text-slate-600">{approved
        ? 'Their listing is live free of charge and the public counter updated. They have been welcomed by email.'
        : 'Their listing stays offline. They have been notified with the support contact.'}</p>
      {approved && remaining != null && (
        <p className="mt-3 text-sm font-semibold text-emerald-700">{remaining} founding {remaining === 1 ? 'seat remains' : 'seats remain'}.</p>
      )}
    </div></Shell>
  }

  const seats = data.seats || {}
  return <Shell>
    <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
      <h1 className="text-xl font-bold tracking-tight">Founding Membership application</h1>
      <p className="mt-1 text-sm text-slate-500">One review covers the bar check and the founding decision. Approving puts their listing live — free.</p>
      <dl className="mt-6 space-y-2 text-sm">
        <Row label="Name" value={data.name} />
        <Row label="Firm" value={data.firm} />
        <Row label="Bar number" value={data.bar_number} />
        <Row label="Email" value={data.email} />
      </dl>
      <p className="mt-5 text-sm text-slate-600">
        Founding seats: {seats.founding_members}/{seats.founding_seats} approved · {seats.founding_remaining} left
      </p>
      <a href={CO_SEARCH} target="_blank" rel="noopener noreferrer"
        className="mt-3 inline-block text-sm font-semibold text-emerald-700 hover:underline">Look up on the Colorado registry ↗</a>
      {error && <p className="mt-4 text-sm text-red-600">{error}</p>}
      <div className="mt-7 flex flex-wrap gap-3">
        <button onClick={() => decide('approve')} disabled={!!busy}
          className="flex-1 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-5 py-3 text-sm font-bold text-white shadow transition hover:-translate-y-0.5 disabled:opacity-60">
          {busy === 'approve' ? 'Saving…' : 'Approve founding membership'}
        </button>
        <button onClick={() => decide('decline')} disabled={!!busy}
          className="flex-1 rounded-xl border border-slate-300 px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60">
          {busy === 'decline' ? 'Saving…' : 'Decline'}
        </button>
      </div>
    </div>
  </Shell>
}

export default FoundingReview
